/**
 * MobileTapStepInput - Fast tap-step input for mobile scale readings
 *
 * Replaces the DivisionScrollPicker modal with an inline interface:
 * - Large up/down buttons for ±1 division
 * - Quick adjustment chips: [-5d], [-1d], [=TL], [+1d], [+5d]
 * - Tap-to-type editable input with inputMode="decimal"
 * - Division-aware stepping and capacity clamping
 * - Haptic feedback on changes
 *
 * Props:
 * - value: Current reading value (string)
 * - onChange: Callback when value changes (receives formatted string)
 * - testLoad: The applied test weight
 * - division: Scale's minimum division (e.g., "20 lb", 0.05)
 * - unit: Unit of measurement (lb, kg, g)
 * - capacity: Scale capacity (numeric, already parsed)
 * - disabled: Whether input is disabled
 * - label: Accessibility label
 * - divisionNum: Parsed numeric division value
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Chip,
  Typography
} from '@mui/material';
import {
  KeyboardArrowUp,
  KeyboardArrowDown
} from '@mui/icons-material';
import {
  parseDivision,
  formatToDivision,
  isValidDivisionValue,
  roundToDivision,
  getDecimalPlaces
} from '../../utils/divisionUtils';

const triggerHaptic = () => {
  if (typeof navigator !== 'undefined' && navigator.vibrate) {
    navigator.vibrate(10);
  }
};

const MobileTapStepInput = React.memo(({
  value,
  onChange,
  testLoad,
  division,
  unit = 'lb',
  capacity = 10000,
  disabled = false,
  label = '',
  divisionNum: divisionNumProp
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const divisionNum = useMemo(
    () => divisionNumProp || parseDivision(division),
    [divisionNumProp, division]
  );
  const decimals = useMemo(() => getDecimalPlaces(division), [division]);

  // Step the value by N divisions
  const stepValue = useCallback((numDivisions) => {
    if (disabled) return;

    const currentNum = (value === '' || value === null || value === undefined)
      ? (testLoad != null ? parseFloat(testLoad) : 0)
      : parseFloat(value);

    if (isNaN(currentNum)) return;

    const delta = numDivisions * divisionNum;
    let newValue = roundToDivision(currentNum + delta, division);

    // Clamp to [0, capacity]
    newValue = Math.max(0, Math.min(capacity, newValue));

    const formatted = newValue.toFixed(decimals);
    triggerHaptic();
    onChange(formatted);
  }, [value, testLoad, divisionNum, division, capacity, decimals, disabled, onChange]);

  // Snap to test load
  const snapToTestLoad = useCallback(() => {
    if (disabled) return;
    if (testLoad == null) return;

    const tlNum = parseFloat(testLoad);
    if (isNaN(tlNum)) return;

    const rounded = roundToDivision(tlNum, division);
    const clamped = Math.max(0, Math.min(capacity, rounded));
    const formatted = clamped.toFixed(decimals);
    triggerHaptic();
    onChange(formatted);
  }, [testLoad, division, capacity, decimals, disabled, onChange]);

  // Handle tap on value to enter edit mode
  const handleStartEdit = useCallback(() => {
    if (disabled) return;
    setEditValue(value || '');
    setIsEditing(true);
  }, [disabled, value]);

  // Handle edit input change
  const handleEditChange = useCallback((e) => {
    setEditValue(e.target.value);
  }, []);

  // Handle blur - commit the edit
  const handleEditBlur = useCallback(() => {
    setIsEditing(false);

    if (editValue === '') {
      onChange('');
      return;
    }

    const numValue = parseFloat(editValue);
    if (isNaN(numValue)) {
      onChange('');
      return;
    }

    // Round to nearest division and clamp
    if (!isValidDivisionValue(numValue, division)) {
      const rounded = roundToDivision(numValue, division);
      const clamped = Math.max(0, Math.min(capacity, rounded));
      const formatted = clamped.toFixed(decimals);
      onChange(formatted);
    } else {
      const clamped = Math.max(0, Math.min(capacity, numValue));
      const formatted = clamped.toFixed(decimals);
      onChange(formatted);
    }
  }, [editValue, division, capacity, decimals, onChange]);

  // Handle Enter key to commit
  const handleEditKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }, []);

  // Display value with unit
  const displayValue = useMemo(() => {
    if (value === '' || value === null || value === undefined) {
      return testLoad != null ? formatToDivision(testLoad, division) : '0';
    }
    return value;
  }, [value, testLoad, division]);

  const hasValue = value !== '' && value !== null && value !== undefined;

  // Quick chip definitions
  const chips = useMemo(() => [
    { label: '-5d', step: -5 },
    { label: '-1d', step: -1 },
    { label: '=TL', step: null, action: 'snapTL' },
    { label: '+1d', step: 1 },
    { label: '+5d', step: 5 }
  ], []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        width: '100%'
      }}
    >
      {/* Up Button */}
      <IconButton
        onClick={() => stepValue(1)}
        disabled={disabled}
        aria-label="Increase by one division"
        sx={{
          minHeight: 60,
          minWidth: 60,
          borderRadius: 2,
          bgcolor: 'action.hover',
          '&:active': { bgcolor: 'action.selected' }
        }}
      >
        <KeyboardArrowUp sx={{ fontSize: 36 }} />
      </IconButton>

      {/* Value Display / Input */}
      {isEditing ? (
        <TextField
          value={editValue}
          onChange={handleEditChange}
          onBlur={handleEditBlur}
          onKeyDown={handleEditKeyDown}
          autoFocus
          size="small"
          inputProps={{
            inputMode: 'decimal',
            'aria-label': label || `Reading input, division ${divisionNum} ${unit}`,
            style: { textAlign: 'center', fontSize: '1.25rem' }
          }}
          sx={{ width: 140 }}
        />
      ) : (
        <Box
          onClick={handleStartEdit}
          role="button"
          tabIndex={0}
          aria-label={label || `Current value ${displayValue} ${unit}, tap to edit`}
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'center',
            gap: 0.5,
            cursor: disabled ? 'default' : 'pointer',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            px: 2,
            py: 1,
            minWidth: 140,
            bgcolor: 'background.paper',
            opacity: hasValue ? 1 : 0.6
          }}
        >
          <Typography
            variant="h6"
            component="span"
            sx={{
              fontWeight: hasValue ? 600 : 400,
              fontStyle: hasValue ? 'normal' : 'italic',
              fontSize: '1.25rem'
            }}
          >
            {displayValue}
          </Typography>
          <Typography
            variant="body2"
            component="span"
            color="text.secondary"
          >
            {unit}
          </Typography>
        </Box>
      )}

      {/* Down Button */}
      <IconButton
        onClick={() => stepValue(-1)}
        disabled={disabled}
        aria-label="Decrease by one division"
        sx={{
          minHeight: 60,
          minWidth: 60,
          borderRadius: 2,
          bgcolor: 'action.hover',
          '&:active': { bgcolor: 'action.selected' }
        }}
      >
        <KeyboardArrowDown sx={{ fontSize: 36 }} />
      </IconButton>

      {/* Quick Adjustment Chips */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          flexWrap: 'wrap',
          justifyContent: 'center',
          mt: 0.5
        }}
      >
        {chips.map((chip) => (
          <Chip
            key={chip.label}
            label={chip.label}
            size="small"
            variant={chip.action === 'snapTL' ? 'filled' : 'outlined'}
            color={chip.action === 'snapTL' ? 'primary' : 'default'}
            disabled={disabled}
            onClick={() => {
              if (chip.action === 'snapTL') {
                snapToTestLoad();
              } else {
                stepValue(chip.step);
              }
            }}
            sx={{
              minWidth: 44,
              minHeight: 36,
              fontWeight: chip.action === 'snapTL' ? 700 : 500,
              fontSize: '0.8rem'
            }}
          />
        ))}
      </Box>
    </Box>
  );
});

MobileTapStepInput.displayName = 'MobileTapStepInput';

export default MobileTapStepInput;
